import api from './api';

// Admin service for system administration
export interface ActionResponse {
  success: boolean;
  message: string;
}

export interface ImpersonationActivityLog {
  id: number;
  session_id: number;
  admin_email: string;
  impersonated_email: string;
  endpoint: string;
  http_method: string;
  action: string;
  ip_address?: string;
  user_agent?: string;
  request_params?: string;
  created_at: string;
}

export interface ImpersonationActivityLogsResponse {
  logs: ImpersonationActivityLog[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface ImpersonationActivityStatsResponse {
  total_logs: number;
  total_sessions: number;
  active_sessions: number;
  recent_actions_24h: number;
  by_admin: Record<string, number>;
  by_impersonated_user: Record<string, number>;
  by_action: Record<string, number>;
  oldest_log: string | null;
  newest_log: string | null;
}

class AdminService {
  private baseUrl = '/admin';

  async getDriverDispatchSyncDebug(): Promise<DriverDispatchSyncDebugResponse> {
    const response = await api.get(`${this.baseUrl}/driver-dispatch-sync/debug`);
    return response.data;
  }

  async getDatabaseMonitor(signal?: AbortSignal): Promise<DatabaseMonitorResponse> {
    const response = await api.get(`${this.baseUrl}/database/monitor`, { signal });
    return response.data;
  }

  async getQuerySniffer(server: string, limit: number = 100, signal?: AbortSignal): Promise<QuerySnifferResponse> {
    const response = await api.get(`${this.baseUrl}/database/query-sniffer`, {
      params: { server, limit },
      signal
    });
    return response.data;
  }

  async getSlowQueryLog(limit: number = 50, hours: number = 24, signal?: AbortSignal): Promise<SlowQueryLogResponse> {
    const response = await api.get(`${this.baseUrl}/database/slow-query-log`, {
      params: { limit, hours },
      signal
    });
    return response.data;
  }

  async getSystemLogs(params?: {
    level?: string;
    category?: string;
    search?: string;
    /** Only entries newer than this many hours. */
    hours?: number;
    limit?: number;
    offset?: number;
  }, signal?: AbortSignal): Promise<SystemLogsResponse> {
    const response = await api.get(`${this.baseUrl}/system-logs`, { params, signal });
    return response.data;
  }

  async getSystemLogsStats(signal?: AbortSignal): Promise<SystemLogsStatsResponse> {
    const response = await api.get(`${this.baseUrl}/system-logs/stats`, { signal });
    return response.data;
  }

  async clearSystemLogs(): Promise<{ message: string; deleted_count: number }> {
    const response = await api.delete(`${this.baseUrl}/system-logs/clear`);
    return response.data;
  }

  async deleteSystemLog(logId: number): Promise<{ message: string; deleted_count: number }> {
    const response = await api.delete(`${this.baseUrl}/system-logs/entry`, {
      params: { log_id: logId },
    });
    return response.data;
  }

  async getSimilarSystemLogCount(
    logId: number,
    signal?: AbortSignal
  ): Promise<SystemLogCodeSiteCount> {
    const response = await api.get(`${this.baseUrl}/system-logs/similar-count`, {
      params: { log_id: logId },
      signal,
    });
    return response.data;
  }

  async deleteSimilarSystemLogs(logId: number): Promise<SystemLogSimilarDeleteResponse> {
    const response = await api.delete(`${this.baseUrl}/system-logs/similar`, {
      params: { log_id: logId },
    });
    return response.data;
  }

  async getAuditLogs(params?: {
    admin_email?: string;
    action?: string;
    target_user_email?: string;
    is_sensitive?: boolean;
    search?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }, signal?: AbortSignal): Promise<AdminAuditLogsResponse> {
    const response = await api.get(`${this.baseUrl}/audit-logs`, { params, signal });
    return response.data;
  }

  async getAuditLogsStats(signal?: AbortSignal): Promise<AdminAuditLogsStatsResponse> {
    const response = await api.get(`${this.baseUrl}/audit-logs/stats`, { signal });
    return response.data;
  }

  async getImpersonationActivity(params?: {
    admin_email?: string;
    impersonated_email?: string;
    session_id?: number;
    search?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }): Promise<ImpersonationActivityLogsResponse> {
    const response = await api.get(`${this.baseUrl}/impersonation-activity`, { params });
    return response.data;
  }

  async getImpersonationActivityStats(): Promise<ImpersonationActivityStatsResponse> {
    const response = await api.get(`${this.baseUrl}/impersonation-activity/stats`);
    return response.data;
  }

  async getSecurityLogs(params?: {
    event_type?: string;
    user_email?: string;
    ip_address?: string;
    severity?: string;
    search?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }): Promise<SecurityAuditLogsResponse> {
    const response = await api.get(`${this.baseUrl}/security-logs`, { params });
    return response.data;
  }

  async getSecurityLogsStats(): Promise<SecurityAuditLogsStatsResponse> {
    const response = await api.get(`${this.baseUrl}/security-logs/stats`);
    return response.data;
  }

  async clearSecurityLogs(): Promise<{ message: string; deleted_count: number }> {
    const response = await api.delete(`${this.baseUrl}/security-logs/clear`);
    return response.data;
  }
}

export interface DriverRow {
  pronumber: string | null;
  dispatchstarted: string | null;
  origarrivaldatetime: string | null;
  arrivaldatetime: string | null;
  departdatetime: string | null;
  origname: string | null;
  destname: string | null;
}

export interface DriverDebugInfo {
  unitnumber: string;
  drivername: string;
  total_rows: number;
  driver_rows: DriverRow[];
  started_dispatches: DriverRow[];
  decision: string;
  selected_row: {
    pronumber: string | null;
    origname: string | null;
    destname: string | null;
    origarrivaldatetime?: string;
  } | null;
}

export interface DriverDispatchSyncDebugResponse {
  total_drivers: number;
  total_raw_results: number;
  debug_info: DriverDebugInfo[];
  timestamp: string;
}

export interface RunningQuery {
  id: number;
  user: string;
  host: string;
  database: string | null;
  command: string;
  time: number;
  state: string | null;
  query: string | null;
}

export interface TableInfo {
  name: string;
  rows: number;
  total_size_mb: number;
  data_size_mb: number;
  index_size_mb: number;
}

export interface ReplicationStatus {
  io_running: string;
  sql_running: string;
  seconds_behind_master: number;
  last_error: string;
}

export interface DatabaseInfo {
  name: string;
  host: string;
  type: 'primary' | 'replica';
  status: 'connected' | 'error';
  response_time_ms?: number;
  connections?: number;
  active_queries?: number;
  slow_queries?: number;
  database_size_mb?: number;
  uptime?: string;
  uptime_seconds?: number;
  running_queries?: RunningQuery[];
  top_tables?: TableInfo[];
  replication_status?: ReplicationStatus | null;
  error?: string;
}

export interface DatabaseMonitorResponse {
  timestamp: string;
  databases: DatabaseInfo[];
}

export interface QuerySnifferData {
  query_pattern: string;
  execution_count: number;
  avg_time_sec: number;
  max_time_sec: number;
  total_time_sec: number;
  avg_rows_examined: number;
  avg_rows_sent: number;
  first_seen: string | null;
  last_seen: string | null;
  database?: string;
  user?: string;
  host?: string;
  source: 'performance_schema' | 'processlist_snapshot';
}

export interface QuerySnifferResponse {
  server: string;
  host: string;
  timestamp: string;
  query_count: number;
  queries: QuerySnifferData[];
}

export interface SlowQueryLogEntry {
  id: number;
  captured_at: string;
  query_count: number;
  avg_time_seconds: number;
  total_time_seconds: number;
  avg_rows_examined: number;
  avg_rows_sent: number;
  query_pattern: string;
  user_host: string;
  database_name: string;
}

export interface SlowQueryLogResponse {
  timestamp: string;
  hours: number;
  total_count: number;
  returned_count: number;
  queries: SlowQueryLogEntry[];
}

export interface SystemLog {
  id: number;
  timestamp: string;
  level: string;
  logger_name: string;
  message: string;
  module?: string;
  function?: string;
  line_number?: number;
  process_id?: number;
  thread_id?: number;
  exception_type?: string;
  exception_message?: string;
  stack_trace?: string;
  category?: string;
}

export interface SystemLogsResponse {
  logs: SystemLog[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

/** A logging call site: logger_name + function + line_number. */
export interface SystemLogCodeSite {
  logger_name: string;
  function: string | null;
  line_number: number | null;
}

export interface SystemLogCodeSiteCount extends SystemLogCodeSite {
  count: number;
}

export interface SystemLogSimilarDeleteResponse extends SystemLogCodeSite {
  message: string;
  deleted_count: number;
}

export interface SystemLogsStatsResponse {
  total_logs: number;
  recent_errors_24h: number;
  by_level: Record<string, number>;
  by_category: Record<string, number>;
  oldest_log: string | null;
  newest_log: string | null;
}

export interface AdminAuditLog {
  id: number;
  admin_email: string;
  action: string;
  endpoint?: string;
  http_method?: string;
  target_user_email?: string;
  resource?: string;
  permission_action?: string;
  ip_address?: string;
  user_agent?: string;
  request_body?: string;
  request_params?: string;
  response_status?: number;
  is_sensitive_operation: boolean;
  bypass_reason: string;
  created_at: string;
}

export interface AdminAuditLogsResponse {
  logs: AdminAuditLog[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface AdminAuditLogsStatsResponse {
  total_logs: number;
  sensitive_operations: number;
  recent_actions_24h: number;
  recent_sensitive_24h: number;
  by_admin: Record<string, number>;
  by_action: Record<string, number>;
  oldest_log: string | null;
  newest_log: string | null;
}

export interface SecurityAuditLog {
  id: number;
  event_type: string;
  user_email?: string;
  ip_address?: string;
  user_agent?: string;
  endpoint?: string;
  http_method?: string;
  details?: Record<string, unknown>;
  severity: 'low' | 'medium' | 'high' | 'critical';
  created_at: string;
}

export interface SecurityAuditLogsResponse {
  logs: SecurityAuditLog[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface SecurityAuditLogsStatsResponse {
  total_events: number;
  high_critical_count: number;
  recent_events_24h: number;
  recent_high_critical_24h: number;
  by_event_type: Record<string, number>;
  by_severity: Record<string, number>;
  by_ip: Record<string, number>;
  oldest_event: string | null;
  newest_event: string | null;
}

const adminService = new AdminService();
export default adminService;
